import { useEffect, useState } from 'react';

import { useConfigValue } from 'hooks/configs/use-config-value';

declare global {
  interface Window {
    OneTrust: any;
  }
}

type ErrorState = ErrorEvent | null;

const IS_BROWSER = typeof window !== 'undefined' && typeof window.document !== 'undefined';
const ONE_TRUST_SDK_SCRIPT_ID = 'one-trust-cookie-consent-sdk';

/**
 * Loads one trust sdk cookie consent script
 */
export default function useOneTrustSdk() {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<ErrorState>(null);
  const cookieCompliance = useConfigValue({ key: 'oneTrust', defaultValue: {} }).cookieCompliance;

  useEffect(() => {
    const { scriptUrl, scriptId } = cookieCompliance;
    if (!IS_BROWSER || !scriptUrl || !scriptId) {
      return;
    }

    const existing = document.querySelectorAll(`script#${ONE_TRUST_SDK_SCRIPT_ID}`);

    if (existing.length > 0) {
      setLoading(false);
      setLoaded(true);
      return;
    }

    const handleLoad = () => {
      setLoading(false);
      setLoaded(true);

      // This code block is required by One Trust script which is called after the SDK is loaded
      // @ts-expect-error TS(6133) FIXME: 'OptanonWrapper' is declared but its value is neve... Remove this comment to see the full error message
      function OptanonWrapper() {} // eslint-disable-line @typescript-eslint/no-unused-vars
    };

    const handleError = (err: ErrorState) => {
      setLoading(false);
      setLoaded(false);
      setError(err);
    };

    const otSdkScript = document.createElement('script');
    // TODO: This will be updated to include live endpoints based on region.
    otSdkScript.setAttribute('src', scriptUrl);
    otSdkScript.setAttribute('data-domain-script', scriptId);
    otSdkScript.setAttribute('id', ONE_TRUST_SDK_SCRIPT_ID);
    otSdkScript.setAttribute('type', 'text/javascript');
    otSdkScript.setAttribute('charset', 'UTF-8');
    otSdkScript.setAttribute('crossOrigin', 'anonymous');
    otSdkScript.async = true;
    otSdkScript.addEventListener('load', handleLoad);
    otSdkScript.addEventListener('error', handleError);

    document.head.appendChild(otSdkScript);

    return () => {
      document.head.removeChild(otSdkScript);
      otSdkScript.removeEventListener('load', handleLoad);
      otSdkScript.removeEventListener('error', handleError);
    };
  }, [cookieCompliance]);

  return { loaded, loading, error };
}
